/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, JSX } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/IllustratedMessage/IllustratedMessage.css';
import { Card } from '@adobe-commerce/elsie/components';

export interface IllustratedMessageProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'icon' | 'action'> {
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  heading?: string;
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  message?: VNode<HTMLAttributes<HTMLElement>>;
  action?: VNode;
  variant?: 'primary' | 'secondary';
}

export const IllustratedMessage: FunctionComponent<IllustratedMessageProps> = ({
  className,
  icon,
  heading,
  headingLevel = 2,
  message,
  action,
  variant = 'secondary',
  ...props
}) => {
  const Heading =
    headingLevel >= 1 && headingLevel <= 6
      ? (`h${headingLevel}` as keyof JSX.IntrinsicElements)
      : 'h2';

  return (
    <div
      {...props}
      className={classes(['dropin-illustrated-message', className])}
    >
      <Card variant={variant}>
        {icon && (
          <VComponent
            node={icon}
            aria-hidden="true"
            size="80"
            className="dropin-illustrated-message__icon"
          />
        )}

        {heading && (
          <Heading className="dropin-illustrated-message__heading">
            {heading}
          </Heading>
        )}

        {message && (
          <VComponent
            node={message}
            className="dropin-illustrated-message__message"
          />
        )}

        {action && (
          <VComponent
            node={action}
            className="dropin-illustrated-message__action"
          />
        )}
      </Card>
    </div>
  );
};
